import { TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { ISSUE_PARAM, trackerUrls } from '@/tracker/config/urls';
import { isMac } from '@/utils/isMac';

import { CreateIssueAside } from '../CreateIssueAside';
import { QueueSelect } from '../QueueSelect';
import { CreateIssueStore } from '../../model/CreateIssueStore';

import { getInitialParent } from './getInitialParent';
import { CreateIssueFormValues } from './types';

import s from './CreateIssueForm.module.scss';

/** Создание задачи как в Яндекс Трекере: название и описание слева, параметры справа */
const CreateIssueForm = observer(() => {
  const alertsStore = useAlertsStore();
  const { store } = useLocalStore(() => new CreateIssueStore({ alertsStore }));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialQueue = searchParams.get('queue') ?? '';
  // Из колонки доски: доска, статусы колонки через запятую, спринт с доски и уже введённое название
  const initialBoardId = searchParams.get('board');
  const initialStatuses = searchParams.get('status') ?? '';
  const initialParent = getInitialParent({
    key: searchParams.get('parent'),
    state: location.state,
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateIssueFormValues>({
    defaultValues: {
      queue: initialQueue,
      summary: searchParams.get('summary') ?? '',
      description: '',
      type: '',
      status: '',
      assignee: null,
      parent: initialParent,
      board: null,
      sprint: searchParams.get('sprint') ?? '',
    },
  });

  useEffect(() => {
    store.load();
    store.selectQueue(initialQueue || null);
  }, [store, initialQueue]);

  useEffect(() => {
    if (!initialBoardId) return;

    store.selectBoard(initialBoardId);
    store.loadBoard(initialBoardId).then((board) => board && setValue('board', board));
  }, [store, initialBoardId, setValue]);

  // Тип и статус по умолчанию — из выбранной очереди, как в Яндекс Трекере; спринты у очереди свои
  useEffect(() => {
    const { queue } = store;

    if (!queue) return;

    // Из колонки доски — её статус, если он есть у очереди
    const columnStatus = initialStatuses
      .split(',')
      .find((key) => queue.statuses.some((status) => status.key === key));

    setValue('type', queue.defaultType.key);
    setValue('status', columnStatus ?? queue.statuses[0]?.key ?? '');

    if (!store.sprints.some((sprint) => sprint.id === getValues('sprint'))) {
      setValue('sprint', '');
    }
  }, [store, store.queue, initialStatuses, setValue, getValues]);

  const submit = handleSubmit(async (values) => {
    const { queue, summary, description, type, status, assignee, parent, board, sprint } = values;
    const task = await store.create({
      data: {
        queue,
        summary: summary.trim(),
        ...(description.trim() && { description: description.trim() }),
        ...(type && { type }),
        ...(status && { status }),
        ...(assignee && { assignee: assignee.id }),
        ...(parent && { parent: parent.key }),
      },
      board,
      sprint: store.sprints.find((item) => item.id === sprint) ?? null,
    });

    if (!task) return;

    // Созданная задача открывается карточкой поверх доски, откуда пришли, или своей очереди
    const pathname = initialBoardId
      ? trackerUrls.board(initialBoardId)
      : trackerUrls.queue(task.queue.key);

    navigate({ pathname, search: `?${ISSUE_PARAM}=${task.key}` }, { replace: true });
  });

  useHotkeys('mod+enter', () => submit(), { enableOnFormTags: true });

  // Читаем здесь, а не в render-функциях Controller: они выполняются вне observer
  const { queues, isLoading } = store;

  // Открыли форму по прямой ссылке — «назад» вывел бы из приложения
  const cancel = () => (location.key === 'default' ? navigate(trackerUrls.root) : navigate(-1));

  return (
    <form className={s.page} onSubmit={submit}>
      <header className={s.page__header}>
        <Controller
          control={control}
          name="queue"
          rules={{ required: 'Выберите очередь' }}
          render={({ field }) => (
            <QueueSelect
              value={field.value}
              onChange={(key) => {
                field.onChange(key);
                store.selectQueue(key || null);
              }}
              queues={queues}
              isLoading={isLoading}
              isError={!!errors.queue}
            />
          )}
        />
        <h1 className={s.page__title}>Новая задача</h1>
      </header>

      <div className={s.page__body}>
        <div className={s.page__main}>
          <TextField
            fullWidth
            autoFocus
            placeholder="Название задачи"
            error={!!errors.summary}
            helperText={errors.summary?.message}
            {...register('summary', {
              validate: (value) => !!value.trim() || 'Введите название',
            })}
            slotProps={{ htmlInput: { 'aria-label': 'Название задачи', className: s.summary } }}
          />
          <TextField
            fullWidth
            multiline
            minRows={8}
            placeholder="Описание: разметка как в Яндекс Трекере"
            {...register('description')}
            slotProps={{ htmlInput: { 'aria-label': 'Описание' } }}
          />
          <div className={s.page__actions}>
            <Button type="submit" disabled={store.isSaving}>
              Создать задачу
            </Button>
            <Button variant="text" onClick={cancel}>
              Отменить
            </Button>
            <span className={s.page__hint}>{isMac ? '⌘' : 'Ctrl'} + Enter</span>
          </div>
        </div>

        <CreateIssueAside store={store} control={control} setValue={setValue} />
      </div>
    </form>
  );
});

export { CreateIssueForm };
